// The customer's devices in one list (ADR-0085, #580).
//
// **A component of its own, mounted where the Devices page used to say "pick a
// site".** Everything that page does besides listing — claim, import, edit,
// the acts, the console, the topology, carry-over, convergence, client counts
// — is site-scoped and stays that way; ADR-0085 decision 5 adds a read and no
// verb at customer scope. Grafting a second scope into that page would have
// put a `siteId ? … : …` on every one of them.
//
// **The sites section is not decoration.** A customer read is a fan-out, so
// this list can be short for reasons that have nothing to do with the customer
// having few devices: a cell that did not answer, a site with no placement,
// sites past the read's bound. Decision 3 makes every one of those a named row
// rather than a silent absence, and rendering the devices without them would
// put the page back where the decision started.
import { useState } from "react";
import { api } from "../../api/client";
import { useDrained } from "../../api/hooks";
import { EntityRef, IdRef } from "../../shell/EntityRef";
import type { Entity } from "../../shell/entity";
import { ageOf, useReportedAge } from "../../shell/freshness";
import { ScopeLink } from "../../shell/scope";
import { Card, Empty, Info, Loading, Pill, ProblemBox, TableSkeleton } from "../../ui";
import type { components } from "../../api/schema";
import { STATUS_RANK, StatusState, statusOf } from "./DevicesTable";
import Incomplete, { incompleteWords } from "./Incomplete";
import { LIVENESS, matchesText, nextSort, sortRows, SortHeader, type Liveness, type Sort, type SortValue } from "./narrow";
import { listed, unrecognized, categoryWord } from "../../api/openEnum";

type Status = components["schemas"]["CustomerSiteReadStatus"];
type Page = components["schemas"]["CustomerDevicePage"];
type Row = Page["items"][number];

/** The columns this list sorts on (#1259). */
type SortKey = "serial" | "name" | "site" | "type" | "status";
const BY: Record<SortKey, (d: Row) => SortValue> = {
  serial: (d) => d.serial,
  name: (d) => d.name,
  site: (d) => d.site_name,
  type: (d) => d.category,
  // `statusOf`'s word, in the site tables' order (#1265): read from
  // `online_state` and `presence` as served, so `unknown` sorts apart from
  // `offline`, which the `online` boolean — `false` for both — could not.
  status: (d) => STATUS_RANK[statusOf(d).word],
};

/** A customer read's `sites` is per page — each page re-runs the fan-out — so
 * the list's sites are every page's, and a site any page could not read is
 * not read: its devices past that page's cursor are not in the list. */
function combineSites(acc: Page, next: Page): Page {
  const sites = acc.sites.map((s) => {
    const later = next.sites.find((n) => n.site_id === s.site_id);
    return later && later.status !== "read" && s.status === "read" ? later : s;
  });
  for (const n of next.sites) if (!sites.some((s) => s.site_id === n.site_id)) sites.push(n);
  return { ...acc, sites };
}

type View = { key: string; sort?: Sort<SortKey>; q: string; status: "" | Liveness; site: string };
const fresh = (key: string): View => ({ key, q: "", status: "", site: "" });
const LIVENESS_LABEL: Record<Liveness, string> = { online: "Online", offline: "Offline", unknown: "Unknown" };

/** How each status reads on the page, and how loudly. `read` never appears —
 * a site that answered is not news; it is the devices. */
const SITES: Record<Exclude<Status, "read">, { label: string; tone: "warn" | "serious" | "info"; says: string }> = {
  unconfirmed: {
    label: "Placement unconfirmed", tone: "info",
    says: "These devices are live. What is unconfirmed is where the site is placed — configuration writes are frozen for it until that is reconfirmed.",
  },
  moving: {
    label: "Migrating", tone: "info",
    says: "The site is moving between controllers. Its devices are listed, read from the controller named here.",
  },
  unplaced: {
    label: "No controller", tone: "warn",
    says: "The site has no controller, so it has no devices to read. Nothing was asked.",
  },
  unanswered: {
    label: "Did not answer", tone: "serious",
    says: "This site’s controller could not be reached, so none of its devices are in this list — including its switches. This is not a site with no devices.",
  },
  truncated: {
    label: "Not read", tone: "warn",
    says: "This list stopped at its limit before reaching this site. The site was not asked and is not known to be empty.",
  },
};

export default function CustomerDevices({ customerId }: { customerId: string }) {
  // The query is inside the component that needs it, and the component is
  // mounted only when there is a customer and no site — so there is no scope
  // in which this asks for a customer it does not have.
  //
  // **Read to its end** (#1259): every page, so the count sentence is true of
  // the customer and not of the first page. Each page re-runs the same bounded
  // fan-out, which costs nothing within §9.5's per-cell cache window.
  const q = useDrained(
    (page_token?: string) => api.GET("/customers/{id}/devices", { params: { path: { id: customerId }, query: { page_size: 200, ...(page_token ? { page_token } : {}) } } }),
    [customerId],
    combineSites,
  );
  // How old the oldest copy among the sites that answered is, for the footer
  // (#1315): the page's `listing_as_of` is already that oldest.
  useReportedAge(ageOf("listing", q.data?.listing_as_of));
  const [stored, setView] = useState<View>(() => fresh(customerId));
  const view = stored.key === customerId ? stored : fresh(customerId);
  const update = (patch: Partial<View>) => setView({ ...view, ...patch });

  const head = (
    <div className="page-head">
      {/* What the list spans, behind the ⓘ beside the title (#1900). */}
      <span className="page-title">
        <h1>Devices</h1>
        <Info label="About this list">Across every site of this customer. Choose a site in the scope switcher above to claim, configure or act on a device.</Info>
      </span>
    </div>
  );

  const onSort = (k: SortKey) => update({ sort: nextSort(view.sort, k) });
  // The devices table's header row, once for the table and for the skeleton
  // drawn before the customer's sites answer (#1321), so the two cannot differ.
  const deviceHead = (
    <>
      <SortHeader k="serial" sort={view.sort} onSort={onSort}>Serial</SortHeader>
      <SortHeader k="name" sort={view.sort} onSort={onSort}>Name</SortHeader>
      <SortHeader k="site" sort={view.sort} onSort={onSort}>Site</SortHeader>
      <SortHeader k="type" sort={view.sort} onSort={onSort}>Type</SortHeader>
      <SortHeader k="status" sort={view.sort} onSort={onSort}>Status</SortHeader>
    </>
  );

  // The skeleton stands on the first read and a new customer; a reload that
  // keeps the previous answer says so in the sentence, as it did.
  if (q.status === "loading") return <>{head}{q.data
    ? <Loading label="Loading the customer’s sites" />
    : <><Loading label="Loading the customer’s sites" srOnly /><TableSkeleton label="Devices" head={deviceHead} /></>}</>;
  if (q.status === "error") return <>{head}<ProblemBox problem={q.problem} /></>;
  const page = q.data;
  if (!page) return <>{head}<Loading label="Loading the customer’s sites" /></>;

  const devices = page.items;
  const online = devices.filter((d) => d.online).length;
  // Narrowed, then ordered, over every page. The sentence above the table is
  // about `devices`, and a filter changes neither it nor the sites section.
  const narrowed = view.q.trim() !== "" || view.status !== "" || view.site !== "";
  const rows = sortRows(devices.filter((d) => matchesText(d, view.q)
    && (view.status === "" || d.online_state === view.status)
    && (view.site === "" || d.site_id === view.site)), view.sort, BY);
  // Every site that is not a plain `read`, in the order the server sent them
  // (name order, which is also the order the bound truncates in).
  const notable = page.sites.filter((s) => s.status !== "read");
  const unread = page.sites.filter((s) => s.status === "unanswered" || s.status === "unplaced" || s.status === "truncated");

  return (
    <>
      {head}
      <p className="muted">
        {devices.length} device{devices.length === 1 ? "" : "s"}{page.incomplete ? " read, list incomplete" : ""}, {online} online, across {page.sites.length - unread.length} of {page.sites.length} site
        {page.sites.length === 1 ? "" : "s"}.
        {unread.length > 0 && <> {unread.length} site{unread.length === 1 ? " was" : "s were"} not read — see below.</>}
      </p>

      {notable.length > 0 && (
        <Card className="panel" title="Sites this list could not speak for">
          <table className="table">
            <thead><tr><th>Site</th><th>State</th><th>What that means</th></tr></thead>
            <tbody>
              {notable.map((s) => {
                // Restates ADR-0511 decision 5a — a status this build does not know is shown as received, marked.
                const kind = listed(SITES, s.status) ? SITES[s.status] : { label: unrecognized(s.status), tone: "warn" as const, says: "This page does not recognize what the server said about this site. Reload the page: a newer version may know it." };
                return (
                  <tr key={s.site_id}>
                    <td>{s.name}</td>
                    <td><Pill tone={kind.tone}>{kind.label}</Pill></td>
                    <td className="muted">{s.detail ?? kind.says}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Incomplete problem={page.incomplete} read={devices.length} noun="devices" onRetry={q.reload} />

      {devices.length > 0 && (
        <div className="toolbar">
          <input aria-label="Filter devices" placeholder="Name, serial, model or firmware" value={view.q}
            onChange={(e) => update({ q: e.target.value })} autoComplete="off" spellCheck={false} style={{ width: 280 }} />
          {/* Restates ADR-0219 decisions 1 and 3 — the three answers `online_state` is served
              with, offered as given: `unknown` is a state of its own and not a kind of offline. */}
          <select aria-label="Status" value={view.status} onChange={(e) => update({ status: e.target.value as View["status"] })}>
            <option value="">Any status</option>
            {LIVENESS.map((l) => <option key={l} value={l}>{LIVENESS_LABEL[l]}</option>)}
          </select>
          <select aria-label="Site" value={view.site} onChange={(e) => update({ site: e.target.value })}>
            <option value="">Every site</option>
            {/* The sites that have a device in the list. A site that could not be read
                has none to narrow to, and is named in the section above instead. */}
            {page.sites.filter((s) => devices.some((d) => d.site_id === s.site_id)).map((s) => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
          </select>
          {narrowed && <span className="muted">{rows.length} of {devices.length} shown</span>}
          {(narrowed || view.sort) && <button type="button" className="btn btn-quiet" onClick={() => setView(fresh(customerId))}>Clear</button>}
        </div>
      )}

      {devices.length > 0 && rows.length === 0 && (
        <p>No device matches. {page.incomplete ? incompleteWords(devices.length, "devices") : `This customer has ${devices.length};`} the filter hides {devices.length === 1 ? "it" : "all of them"}.</p>
      )}

      {devices.length === 0 ? (
        <Empty title="No devices to show."
          info={unread.length === page.sites.length
            ? "None of this customer’s sites could be read, so this is not a customer with no devices — the table above says what happened to each."
            : "No devices are claimed to this customer’s sites yet."} />
      ) : rows.length > 0 && (
        <div className="table-wrap"><table aria-label="Devices">
          <thead><tr>{deviceHead}</tr></thead>
          <tbody>
            {rows.map((d) => {
              // The device, its serial and its site each open the one menu (#2367),
              // and each keeps the text it was: this list opens nothing on a click.
              const device: Entity = { kind: "device", serial: d.serial, name: d.name || undefined, mac: d.mac, switch: d.category === "switch", siteId: d.site_id, siteName: d.site_name, customerId };
              return (
              <tr key={d.serial}>
                <td>{d.name ? <IdRef entity={device} value={d.serial} /> : <EntityRef entity={device}><code className="id">{d.serial}</code></EntityRef>}</td>
                <td>{d.name && <EntityRef entity={device}><span>{d.name}</span></EntityRef>}</td>
                <td>{d.site_name && d.site_id
                  ? <EntityRef entity={{ kind: "site", id: d.site_id, name: d.site_name, customerId }}><span>{d.site_name}</span></EntityRef>
                  : d.site_name ?? <span className="muted">—</span>}</td>
                <td>{categoryWord(d.category, true)}</td>
                {/* The site list's words, dot and tooltip (#1265): `online_state` as served,
                    and `absent` for a switch the cell does not hold. The `online` boolean is
                    `false` for both `offline` and `unknown`, so a view rendering it alone is
                    the defect ADR-0219 rules out; the words and their markers are onlineOf's. */}
                <td><StatusState device={d} /></td>
              </tr>
              );
            })}
          </tbody>
        </table></div>
      )}

      {/* Where a device is acted on, behind the ⓘ beside the link that goes there; the link stays in the
          open (#1900). "Choose a site to reach those." joins the words the link used to carry. */}
      <p className="muted">
        <ScopeLink to="/sites">Choose a site</ScopeLink>
        <Info label="About acting on a device">Acting on a device is done at its site: claiming, editing, a command, a console, its ports. Choose a site to reach those.</Info>
      </p>
    </>
  );
}
